import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Check,
  X,
  Mic,
  Lock,
  Fingerprint,
  Link2,
  KeyRound,
  ShieldCheck,
  FileCheck,
  Signature,
  ShieldOff,
  AlertTriangle,
  Battery,
  ExternalLink,
  Youtube,
} from "lucide-react";
import { PageIntro, PageBadgeBand, Section } from "@/components/site/PageShell";
import { SITE } from "@/constants/testIds";
import { useMeta } from "@/hooks/useMeta";
import { detectVisitorState, RCFP_URL } from "@/lib/usStates";
import { trackEvent } from "@/lib/analytics";
import { BRAND } from "@/lib/config";

/* , 1. WHAT WE CAN SEE , */
const CANNOT = [
  {
    label: "The content of your recordings",
    detail:
      "Your audio is encrypted on your phone with AES-256 before it leaves the device.",
  },
  {
    label: "The words that were said",
    detail:
      "We do not transcribe your recordings and cannot read them. There is no “listen in” button on our side.",
  },
  {
    label: "Anyone else's device",
    detail:
      "YeboNadi records only what your microphone captures. It has no reach into other people's phones, laptops, or accounts.",
  },
];

const CAN = [
  {
    label: "Your account email",
    detail: "So we can sign you in and send you receipts. Nothing more.",
  },
  {
    label: "Storage usage",
    detail:
      "How much sealed data your account is holding, so we can enforce your plan limits and rolling overwrite.",
  },
  {
    label: "Seal hashes and timestamps",
    detail:
      "The SHA-256 fingerprints and capture timestamps of your recordings, the fingerprints, not the content.",
  },
  {
    label: "Location metadata and seal fingerprints",
    detail:
      "We can verify a recording's integrity from its GPS, timestamp, and SHA-256 hash, without ever decrypting or listening to the audio itself. Not even for a sound check.",
  },
];

function WhatWeCanTable() {
  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 rounded-2xl overflow-hidden border border-[color:var(--yn-border)]"
      data-testid={SITE.securityCanTable}
    >
      <div className="p-6 sm:p-8 border-b md:border-b-0 md:border-r border-[color:var(--yn-border)] bg-white">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
            <X size={16} strokeWidth={2.5} />
          </span>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">
            What we can&apos;t see
          </p>
        </div>
        <ul className="mt-6 space-y-5">
          {CANNOT.map((item) => (
            <li key={item.label}>
              <p className="font-heading text-lg font-semibold text-[color:var(--yn-ink)] leading-snug">
                {item.label}
              </p>
              <p className="mt-1.5 text-[15px] leading-relaxed text-[color:var(--yn-muted)]">
                {item.detail}
              </p>
            </li>
          ))}
        </ul>
      </div>

      <div className="p-6 sm:p-8 bg-[color:var(--yn-subtle)]">
        <div className="flex items-center gap-2">
          <span
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-white"
            style={{ background: "var(--yn-primary)" }}
          >
            <Check size={16} strokeWidth={2.5} />
          </span>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
            What we can see
          </p>
        </div>
        <ul className="mt-6 space-y-5">
          {CAN.map((item) => (
            <li key={item.label}>
              <p className="font-heading text-lg font-semibold text-[color:var(--yn-ink)] leading-snug">
                {item.label}
              </p>
              <p className="mt-1.5 text-[15px] leading-relaxed text-[color:var(--yn-muted)]">
                {item.detail}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* , 2. HOW SEALING WORKS , */
const SEAL_STEPS = [
  {
    n: "1",
    icon: Mic,
    title: "Capture",
    body: "You start recording manually; the app gathers audio, timestamp, and GPS together in one bundle until you end the session.",
    analogy:
      "Like a photograph, everything present in the frame is captured at once.",
  },
  {
    n: "2",
    icon: Lock,
    title: "Encrypt (AES-256)",
    body: "The bundle is scrambled with a key only your device holds. It is encrypted on your phone, before it leaves.",
    analogy:
      "Like locking the photograph in a safe before it leaves the room.",
  },
  {
    n: "3",
    icon: Fingerprint,
    title: "Upload",
    body: "Data is uploaded immediately (subject to network connectivity). Where the network signals are poor, the data is stored in a secure area on your phone and then transmitted when the data service is restored. Data-transmission is set for 5 minute upload frequency.",
    analogy:
      "Like an outbox that sends what it can, holds what it can't, and never loses the letter.",
  },
  {
    n: "4",
    icon: Link2,
    title: "Chain",
    body: "Every event on the sealed bundle, capture, upload, retrieval request, release, is written to an append-only log. Nothing in that log can be quietly removed but can be overwritten when your storage limits are reached.",
    analogy:
      "Like a paper ledger where you can only add new lines, never erase old ones.",
  },
];

function SealSteps() {
  return (
    <ol
      className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6"
      data-testid={SITE.securitySealSteps}
    >
      {SEAL_STEPS.map((s) => (
        <li
          key={s.n}
          className="relative rounded-2xl border border-[color:var(--yn-border)] p-6 sm:p-7"
        >
          <div className="flex items-center gap-3">
            <span
              className="inline-flex h-9 w-9 items-center justify-center rounded-full text-white font-heading font-bold text-sm"
              style={{ background: "var(--yn-primary)" }}
            >
              {s.n}
            </span>
            <s.icon size={20} style={{ color: "var(--yn-primary)" }} />
          </div>
          <h3 className="mt-5 font-heading text-xl font-semibold text-[color:var(--yn-ink)] leading-snug">
            {s.title}
          </h3>
          <p className="mt-3 text-[15px] leading-relaxed text-[color:var(--yn-muted)]">
            {s.body}
          </p>
          <p
            className="mt-4 pt-4 border-t border-[color:var(--yn-border)] text-[14px] italic text-[color:var(--yn-ink)]/70 leading-relaxed"
          >
            {s.analogy}
          </p>
        </li>
      ))}
    </ol>
  );
}

/* , 4. STALKING FIREWALL , */
const STALKING_POINTS = [
  {
    icon: Mic,
    title: "It records your environment, not anyone else's device.",
    body: "YeboNadi captures what your microphone picks up and where your phone is. It cannot listen into another person's phone, laptop, camera, or account.",
  },
  {
    icon: Signature,
    title: "Every user signs the Undertaking.",
    body: "You cannot make a recording without first signing the Undertaking, the binding pledge not to use YeboNadi for blackmail, harassment, coercion, or unlawful surveillance.",
  },
  {
    icon: FileCheck,
    title: "Retrievals are formal, paid, and logged.",
    body: "There is no way to pull sealed audio out of the app quietly. Every retrieval requires a legal order or signed release, is priced, and is written to the append-only log. If a recording made with YeboNadi ever shows up somewhere it shouldn't, that log is the starting point.",
  },
  {
    icon: ShieldOff,
    title: "Misuse ends the account.",
    body: "Violation of the Undertaking or the Terms is grounds for immediate account termination and, where required by law, cooperation with investigators.",
  },
];

/* , 6. CONSENT LAWS , */
const ALL_PARTY_STATES = [
  "California",
  "Connecticut",
  "Delaware",
  "Florida",
  "Illinois",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Montana",
  "Nevada",
  "New Hampshire",
  "Pennsylvania",
  "Washington",
];

function ConsentExplainer() {
  const [visitor, setVisitor] = useState(null);
  useEffect(() => {
    const v = detectVisitorState();
    setVisitor(v);
    if (v) {
      trackEvent("consent_state_detected", {
        state: v.code,
        source: v.source,
        is_all_party: v.isAllParty,
      });
    }
  }, []);

  return (
    <div
      className="rounded-2xl border border-[color:var(--yn-border)] p-6 sm:p-8"
      data-testid={SITE.securityConsent}
      data-visitor-state={visitor?.code || ""}
    >
      <p className="text-xs font-medium uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
        US consent laws, the short version
      </p>
      <h3 className="mt-3 font-heading text-2xl font-semibold text-[color:var(--yn-ink)] tracking-tight">
        One-party vs. all-party consent.
      </h3>

      {/* Contextual line + external link, only when a state is known */}
      {visitor && (
        <div className="mt-4 rounded-lg border border-[color:var(--yn-primary)]/30 bg-[color:var(--yn-primary)]/5 p-4">
          <p className="text-[15px] leading-relaxed text-[color:var(--yn-ink)]">
            <span className="font-semibold">
              You&apos;re in {visitor.name}
            </span>{" "}
            ,{" "}
            {visitor.isAllParty ? (
              <>
                an <strong>all-party consent</strong> state. Every person in
                the conversation must know and agree before you record.
              </>
            ) : (
              <>
                a <strong>one-party consent</strong> state. You can record a
                conversation you are part of.
              </>
            )}{" "}
            Reading below.
          </p>
          <div className="mt-3 flex items-center gap-4 flex-wrap text-sm">
            <a
              href={RCFP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 font-semibold text-[color:var(--yn-primary)] hover:text-[color:var(--yn-primary-hover)]"
              onClick={() =>
                trackEvent("consent_state_source_click", {
                  state: visitor.code,
                })
              }
            >
              See your state&apos;s law
              <ExternalLink size={13} />
            </a>
            {visitor.guessed && (
              <span className="text-xs text-[color:var(--yn-muted)]">
                Guessed from your browser time zone, override with{" "}
                <code className="px-1 py-0.5 rounded bg-white border border-[color:var(--yn-border)] text-[color:var(--yn-ink)]">
                  ?state=XX
                </code>
              </span>
            )}
          </div>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="rounded-lg bg-[color:var(--yn-subtle)] p-5">
          <p className="font-heading text-lg font-semibold text-[color:var(--yn-ink)]">
            One-party consent
          </p>
          <p className="mt-2 text-[15px] leading-relaxed text-[color:var(--yn-muted)]">
            You can record a conversation you are part of. Federal law and most
            states work this way, you count as the &quot;one party.&quot;
          </p>
        </div>
        <div className="rounded-lg bg-[color:var(--yn-subtle)] p-5">
          <p className="font-heading text-lg font-semibold text-[color:var(--yn-ink)]">
            All-party consent
          </p>
          <p className="mt-2 text-[15px] leading-relaxed text-[color:var(--yn-muted)]">
            Every person in the conversation must know and agree. In these
            states, recording someone secretly is a crime, even if you are
            part of the conversation.
          </p>
        </div>
      </div>

      <p className="mt-6 text-sm font-medium text-[color:var(--yn-ink)]">
        US states that require all-party consent:
      </p>
      <ul className="mt-3 flex flex-wrap gap-2">
        {ALL_PARTY_STATES.map((s) => {
          const isYou = visitor?.name === s;
          return (
            <li
              key={s}
              className={`inline-flex items-center rounded-full px-3 py-1 text-[13px] font-medium transition-colors ${
                isYou
                  ? "bg-[color:var(--yn-primary)] text-white ring-2 ring-[color:var(--yn-primary)] ring-offset-2"
                  : "border border-[color:var(--yn-border)] bg-white text-[color:var(--yn-ink)]"
              }`}
            >
              {s}
              {isYou && (
                <span className="ml-1.5 text-[11px] opacity-90">← you</span>
              )}
            </li>
          );
        })}
      </ul>
      <p className="mt-5 text-xs leading-relaxed text-[color:var(--yn-muted)]">
        Laws change and interpretations vary by court. This list is a starting
        point, not legal advice. If you are unsure, talk to a lawyer in your
        state before you press record. Outside the US, check the recording-consent
        law where you are, most countries have one.
      </p>
    </div>
  );
}

/* , PAGE , */
export default function Security() {
  useMeta({
    title: "Security & Trust, YeboNadi",
    description:
      "Plain-English cryptography for a skeptical reader. What YeboNadi can and can't see, how the seal works (AES-256 + SHA-256 + append-only log), why it is useless for stalking, and where it stops (no legal advice, admissibility depends on jurisdiction).",
    path: "/security",
  });

  return (
    <div data-testid={SITE.securityPage}>
      <PageIntro
        kicker="Security & Trust"
        title="Why you can trust an app you've never heard of."
        lede="Written for the skeptical reader. Every claim on this page is either observable in the app itself, verifiable with a cryptography tool, or in your own hands to control."
      />

      {/* 1. What we can see, and what we can't */}
      <Section className="!pt-0">
        <p className="text-xs font-medium uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
          Transparency
        </p>
        <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold text-[color:var(--yn-ink)] tracking-tight">
          What we can see, and what we can&apos;t.
        </h2>
        <p className="mt-4 max-w-2xl text-[16px] leading-relaxed text-[color:var(--yn-muted)]">
          The most important thing to understand about YeboNadi is that your
          recordings are encrypted on your phone before they ever reach our
          servers. This is what we can, and cannot, do from our side.
        </p>
        <div className="mt-10">
          <WhatWeCanTable />
        </div>
      </Section>

      {/* 2. How sealing works */}
      <Section className="!pt-0">
        <p className="text-xs font-medium uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
          The seal
        </p>
        <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold text-[color:var(--yn-ink)] tracking-tight">
          How sealing works.
        </h2>
        <p className="mt-4 max-w-2xl text-[16px] leading-relaxed text-[color:var(--yn-muted)]">
          Four steps, applied at the moment of capture. Each step has a
          plain-English analogy, because you should not need a crypto degree to
          trust the tool.
        </p>
        <div className="mt-10">
          <SealSteps />
        </div>
      </Section>

      {/* 3. Your evidence is yours */}
      <Section
        className="!pt-0"
      >
        <div
          className="rounded-2xl border border-[color:var(--yn-border)] bg-[color:var(--yn-subtle)] p-6 sm:p-10"
          data-testid={SITE.securityYours}
        >
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
            Your control
          </p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold text-[color:var(--yn-ink)] tracking-tight">
            Your evidence is yours.
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="rounded-xl bg-white p-6 border border-[color:var(--yn-border)]">
              <KeyRound size={20} style={{ color: "var(--yn-primary)" }} />
              <p className="mt-4 font-heading text-lg font-semibold text-[color:var(--yn-ink)] leading-snug">
                Your key, your access.
              </p>
              <p className="mt-3 text-[15px] leading-relaxed text-[color:var(--yn-muted)]">
                Your encryption/decryption key is not derivable or recoverable
                by YeboNadi. Only you hold it. Nothing you sealed can be
                retrieved without it.
              </p>
            </div>
            <div className="rounded-xl bg-white p-6 border border-[color:var(--yn-border)]">
              <ShieldCheck size={20} style={{ color: "var(--yn-primary)" }} />
              <p className="mt-4 font-heading text-lg font-semibold text-[color:var(--yn-ink)] leading-snug">
                Released only by legal order or signed release.
              </p>
              <p className="mt-3 text-[15px] leading-relaxed text-[color:var(--yn-muted)]">
                Sealed recordings can&apos;t be played back inside the app.
                Retrieval requires a legal order or signed release along with
                your encryption/decryption key. YeboNadi decrypts and delivers
                via secure cloud storage. A retrieval fee applies.
              </p>
            </div>
            <div className="rounded-xl bg-white p-6 border border-[color:var(--yn-border)]">
              <Lock size={20} style={{ color: "var(--yn-primary)" }} />
              <p className="mt-4 font-heading text-lg font-semibold text-[color:var(--yn-ink)] leading-snug">
                Originals stay sealed.
              </p>
              <p className="mt-3 text-[15px] leading-relaxed text-[color:var(--yn-muted)]">
                Sealed originals cannot be edited, not by you, not by us, not
                by anyone. Every event on the record is written to an
                append-only log.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* Preparedness callout, share your key with trusted persons */}
      <Section className="!pt-0">
        <div
          className="max-w-3xl rounded-2xl border border-[color:var(--yn-primary)]/30 bg-[color:var(--yn-primary)]/5 p-6 sm:p-8"
          data-testid="site-security-preparedness"
        >
          <div className="flex items-start gap-3">
            <KeyRound
              size={22}
              className="mt-1 shrink-0"
              style={{ color: "var(--yn-primary)" }}
            />
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
                Preparedness, share your key
              </p>
              <p className="mt-3 text-[15px] leading-relaxed text-[color:var(--yn-ink)]">
                Your encryption/decryption key is not recoverable by YeboNadi.
                Share it with one or many very trusted persons you would want
                to have access if something happens to you. Without your key,
                nothing you sealed can be retrieved, even under a legal order.
              </p>
            </div>
          </div>
        </div>
      </Section>

      <PageBadgeBand heading="The seal is only useful if it's already in place." />

      {/* 4. Built to be useless for stalking */}
      <Section data-testid={SITE.securityStalking}>
        <p className="text-xs font-medium uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
          Anti-misuse
        </p>
        <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold text-[color:var(--yn-ink)] tracking-tight">
          Built to be useless for stalking.
        </h2>
        <p className="mt-4 max-w-2xl text-[16px] leading-relaxed text-[color:var(--yn-muted)]">
          A tool this powerful attracts the wrong hands. Four things make
          YeboNadi genuinely bad at the misuse people worry about.
        </p>
        <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          {STALKING_POINTS.map((p) => (
            <li
              key={p.title}
              className="rounded-2xl border border-[color:var(--yn-border)] p-6 sm:p-7"
            >
              <p.icon size={22} style={{ color: "var(--yn-primary)" }} />
              <h3 className="mt-4 font-heading text-lg font-semibold text-[color:var(--yn-ink)] leading-snug">
                {p.title}
              </h3>
              <p className="mt-3 text-[15px] leading-relaxed text-[color:var(--yn-muted)]">
                {p.body}
              </p>
            </li>
          ))}
        </ul>
      </Section>

      {/* Legal-honesty note. */}
      <Section className="!pt-0">
        <div
          className="max-w-3xl"
          data-testid={SITE.securityDeepVault}
        >
          <p className="text-[15px] leading-relaxed text-[color:var(--yn-muted)]">
            <span className="font-semibold text-[color:var(--yn-ink)]">
              Need more storage than Pro?
            </span>{" "}
            Contact us, additional capacity is available on request. Pricing
            shown inside the app. Everyone who uses YeboNadi still signs the
            same{" "}
            <Link
              to="/undertaking"
              className="text-[color:var(--yn-primary)] hover:underline"
            >
              Undertaking
            </Link>
            .
          </p>
        </div>
      </Section>

      {/* 6. Honest limitations */}
      <Section
        className="!pt-0 border-t border-[color:var(--yn-border)]"
        data-testid={SITE.securityLimitations}
      >
        <p className="text-xs font-medium uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
          Honest limits
        </p>
        <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold text-[color:var(--yn-ink)] tracking-tight">
          What YeboNadi is not.
        </h2>

        <div className="mt-8 max-w-3xl rounded-2xl border border-amber-200 bg-amber-50 p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle
              size={20}
              className="mt-0.5 shrink-0 text-amber-700"
            />
            <div className="space-y-3 text-[15px] leading-relaxed text-amber-900">
              <p>
                <strong>Not legal advice.</strong> Nothing on this site or in
                the app is legal advice. If you plan to use a recording as
                evidence, consult a licensed lawyer in your jurisdiction.
              </p>
              <p>
                <strong>Not a guarantee of admissibility.</strong> Whether a
                specific recording is admissible in a specific proceeding
                depends on the law of the applicable jurisdiction and the
                facts of the case. YeboNadi seals what you captured; the court
                decides what it will accept.
              </p>
              <p>
                <strong>Not exempt from consent laws.</strong> You are
                responsible for complying with the recording-consent laws that
                apply where you record. See the explainer below.
              </p>
              <p className="flex items-start gap-2">
                <Battery size={16} className="mt-0.5 shrink-0" />
                <span>
                  <strong>Not immune to your phone.</strong> Battery level, OS
                  power settings, and platform-specific background-recording
                  rules can affect a session. See device walkthroughs on our
                  YouTube channel.
                </span>
              </p>
              <p>
                <a
                  href={BRAND.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackEvent("youtube_click", { placement: "security-limits" })
                  }
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-white border border-amber-300 text-amber-900 font-semibold text-sm hover:bg-amber-100 transition-colors"
                >
                  <Youtube size={16} />
                  Watch on YouTube
                  <ExternalLink size={12} />
                </a>
              </p>
            </div>
          </div>
        </div>

        <div className="mt-10 max-w-3xl">
          <ConsentExplainer />
        </div>
      </Section>

      {/* Small patent line, no hero treatment. */}
      <Section className="!pt-0 !pb-16">
        <p
          className="text-xs text-[color:var(--yn-muted)]"
          data-testid={SITE.securityPatent}
        >
          Patent pending.
        </p>
      </Section>
    </div>
  );
}
